import type { ComponentType } from 'react';
import type { MDXProps } from 'mdx/types';
import type { BlogFrontmatter, BlogPost } from './types';

interface MdxModule {
  default: ComponentType<MDXProps>;
  frontmatter: Partial<BlogFrontmatter>;
}

// Build-time discovery: every `.mdx` file under content/ becomes a post. Adding
// a file here is all it takes for it to appear on the home page and get a route.
const modules = import.meta.glob<MdxModule>('./content/*.mdx', { eager: true });

const REQUIRED: (keyof BlogFrontmatter)[] = [
  'title',
  'excerpt',
  'category',
  'author',
  'date',
  'readTime',
];

function slugFromPath(path: string): string {
  return path.replace(/^.*\/(.+)\.mdx$/, '$1');
}

function toPost(path: string, mod: MdxModule): BlogPost {
  const slug = slugFromPath(path);
  const fm = mod.frontmatter ?? {};
  const missing = REQUIRED.filter((key) => fm[key] == null || fm[key] === '');
  if (missing.length) {
    throw new Error(
      `Blog post "${slug}" is missing required frontmatter: ${missing.join(', ')}`,
    );
  }
  return { ...(fm as BlogFrontmatter), slug, Content: mod.default };
}

/** All posts, newest first. */
export const POSTS: BlogPost[] = Object.entries(modules)
  .map(([path, mod]) => toPost(path, mod))
  .sort((a, b) => b.date.localeCompare(a.date));

/** Look up a single post by its slug. */
export function getPost(slug: string | undefined): BlogPost | undefined {
  return POSTS.find((p) => p.slug === slug);
}

/**
 * Related posts for an article: same category first, then the most recent
 * others, excluding the article itself.
 */
export function getRelated(slug: string, limit = 3): BlogPost[] {
  const current = getPost(slug);
  const others = POSTS.filter((p) => p.slug !== slug);
  const sameCategory = current
    ? others.filter((p) => p.category === current.category)
    : [];
  const rest = others.filter((p) => !sameCategory.includes(p));
  return [...sameCategory, ...rest].slice(0, limit);
}

/** Distinct categories in publish order, prefixed with "All" for the filter bar. */
export const CATEGORIES: string[] = [
  'All',
  ...POSTS.reduce<string[]>((acc, p) => {
    if (!acc.includes(p.category)) acc.push(p.category);
    return acc;
  }, []),
];
